import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { BaseButton, Chip } from '@repo/common';
import { searchMajors } from '../../../api/pathfinder/pathfinder.api';
import { ReactComponent as SearchIcon } from '@repo/common/assets/icons/20px/icon_20px_search.svg';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/16px/icon_16px_plus_outline.svg';

const DEFAULT_MAJOR_SUGGESTIONS = [
  'Business Administration',
  'Computer Science',
  'Economics',
  'Mechanical Engineering',
  'Marketing',
  'Psychology',
  'Visual Design',
];

const normalizeText = (text = '') =>
  text.toLowerCase().replace(/\s+/g, ' ').trim();

function PathfinderMajorSearchSelect({
  value = null,
  onChange,
  selectedLabel: selectedLabelProp = null,
  placeholder = 'Search and select your major',
  showDefaultSuggestions = false,
  defaultSuggestions = DEFAULT_MAJOR_SUGGESTIONS,
}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const closeTimerRef = useRef(null);

  const openPanel = () => {
    clearTimeout(closeTimerRef.current);
    setIsOpen(true);
    requestAnimationFrame(() => setVisible(true));
  };

  const closePanel = () => {
    setVisible(false);
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 200);
  };

  useEffect(() => {
    const handleMouseDown = (event) => {
      if (!rootRef.current?.contains(event.target)) closePanel();
    };

    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      clearTimeout(closeTimerRef.current);
      clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!value) setSelectedLabel(null);
    else if (selectedLabelProp) setSelectedLabel(selectedLabelProp);
  }, [selectedLabelProp, value]);

  const loadSuggestions = useCallback(async (keyword) => {
    try {
      const results = await searchMajors(keyword);
      const mapped = results.map((major) => ({
        id: String(major.id),
        label: major.nameEn,
      }));
      setSuggestions(mapped);
      return mapped;
    } catch {
      setSuggestions([]);
      return [];
    }
  }, []);

  const handleSelect = useCallback(
    (item) => {
      setSelectedLabel(item.label);
      onChange(item.id, {
        selectedOptions: [{ value: item.id, label: item.label }],
      });
      setQuery('');
      setSuggestions([]);
      closePanel();
      inputRef.current?.focus();
    },
    [onChange],
  );

  const handleQueryChange = useCallback(
    (event) => {
      const nextQuery = event.target.value;
      setQuery(nextQuery);

      if (!isOpen) openPanel();

      clearTimeout(debounceRef.current);
      if (!nextQuery || nextQuery.trim().length < 1) {
        setSuggestions([]);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        await loadSuggestions(nextQuery.trim());
      }, 300);
    },
    [isOpen, loadSuggestions],
  );

  const handleDeselect = useCallback(() => {
    setSelectedLabel(null);
    onChange(null, { selectedOptions: [] });
    openPanel();
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [onChange]);

  const handleClearQuery = () => {
    setQuery('');
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const handleDefaultSuggestionClick = useCallback(
    async (label) => {
      const normalizedLabel = normalizeText(label);
      setQuery(label);
      openPanel();
      inputRef.current?.focus();

      const results = await loadSuggestions(label);
      const exactMatch = results.find(
        (item) => normalizeText(item.label) === normalizedLabel,
      );

      if (exactMatch) {
        handleSelect(exactMatch);
      }
    },
    [handleSelect, loadSuggestions],
  );

  const isHighlighted = isOpen;
  const showClear = Boolean(query || value);
  const unselected = suggestions.filter((item) => item.id !== value);
  const showDefaultPanel = showDefaultSuggestions && !query.trim() && !value;
  const showSuggestionPanel =
    isOpen && !showDefaultPanel && (value || unselected.length > 0);

  return (
    <div ref={rootRef} className='flex w-full flex-col gap-[6px]'>
      <div className='flex h-[80px] w-full flex-col items-start justify-center'>
        <div
          className={twMerge(
            'group flex h-full w-full items-center gap-2 rounded-[15px] border-2 bg-surface-lightgray60 py-[10px] pl-4 pr-3 transition-colors duration-200',
            isHighlighted ? 'border-surface-green40' : 'border-transparent',
          )}
          onMouseDown={(event) => {
            if (event.target.closest('button')) return;
            event.preventDefault();
            inputRef.current?.focus();
            openPanel();
          }}
        >
          <SearchIcon
            className={twMerge(
              'size-5 shrink-0 transition-colors duration-200 group-hover:text-green-deep02',
              isHighlighted ? 'text-green-deep02' : 'text-text-placeholder',
            )}
          />
          <div className='flex min-w-0 flex-1 items-center gap-2 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
            {value && selectedLabel && (
              <span className='inline-flex h-[34px] shrink-0 items-center gap-2 rounded-full bg-grayscale-gray07 py-2 pl-3 pr-2'>
                <span className='whitespace-nowrap text-[12px] font-medium leading-[1.4] text-grayscale-gray01'>
                  {selectedLabel}
                </span>
                <BaseButton
                  type='text'
                  size='s'
                  htmlType='button'
                  onClick={handleDeselect}
                  className='size-4 min-w-0 shrink-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent'
                  icon={<CancelIcon className='size-4 text-grayscale-gray01' />}
                />
              </span>
            )}
            <input
              ref={inputRef}
              value={query}
              onChange={handleQueryChange}
              onFocus={() => openPanel()}
              placeholder={value ? '' : placeholder}
              className='min-w-[120px] flex-1 border-none bg-transparent p-0 text-base text-text-primary outline-none placeholder:text-text-placeholder'
            />
          </div>
          {showClear && (
            <BaseButton
              type='text'
              size='s'
              htmlType='button'
              onClick={handleClearQuery}
              className='transition-colors rounded-full size-5 min-w-0 shrink-0 border-0 p-0 bg-grayscale-gray02 text-text-placeholder hover:bg-grayscale-gray03 hover:text-text-placeholder'
              icon={<CancelIcon className='size-4' />}
            />
          )}
        </div>
      </div>

      {showDefaultPanel && (
        <div className='w-full rounded-[8px] border-[1.6px] border-solid border-grayscale-gray02 bg-grayscale-gray00 px-6 pb-6 pt-5'>
          <div className='pb-4'>
            <p className='text-[14px] font-medium leading-[1.4] text-text-primary'>
              Most popular fields :
            </p>
          </div>
          <div className='flex flex-wrap w-full gap-3'>
            {defaultSuggestions.map((label) => (
              <Chip
                key={label}
                type='fill'
                state='default'
                iconPosition='after'
                bgclass='bg-grayscale-gray01'
                borderclass='ring-0'
                onClick={() => handleDefaultSuggestionClick(label)}
              >
                {label}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {showSuggestionPanel && (
        <div
          className={twMerge(
            'origin-top w-full rounded-[15px] border border-grayscale-gray01 bg-grayscale-gray00 px-6 pb-6 pt-5 shadow-3 transition-all duration-200 ease-out',
            visible
              ? 'translate-y-0 scale-y-100 opacity-100'
              : '-translate-y-1 scale-y-95 opacity-0',
          )}
        >
          <div className='flex flex-wrap w-full gap-x-3 gap-y-2'>
            {value && selectedLabel && (
              <BaseButton
                type='text'
                size='m'
                htmlType='button'
                typo={false}
                isStatic
                gap='gap-2'
                iconPosition='after'
                icon={<CancelIcon className='size-4 text-grayscale-gray01' />}
                onClick={handleDeselect}
                textWrapperClass='h-auto flex-none whitespace-nowrap text-[12px] font-medium leading-[1.4] text-grayscale-gray01'
                className='h-[34px] min-w-0 cursor-pointer items-center gap-2 rounded-full border-0 bg-grayscale-gray07 py-2 pl-3 pr-2'
              >
                {selectedLabel}
              </BaseButton>
            )}
            {value && unselected.length > 0 && (
              <div className='w-full h-px my-1 bg-grayscale-gray01' />
            )}
            {unselected.map((item) => (
              <BaseButton
                key={item.id}
                type='text'
                size='m'
                htmlType='button'
                typo={false}
                isStatic
                gap='gap-2'
                iconPosition='after'
                icon={<PlusIcon className='size-4 text-grayscale-gray07' />}
                onClick={() => handleSelect(item)}
                textWrapperClass='h-auto flex-none whitespace-nowrap text-[12px] font-medium leading-[1.4] text-grayscale-gray07'
                className='h-[34px] min-w-0 cursor-pointer items-center gap-2 rounded-full border-0 bg-grayscale-gray01 py-2 pl-3 pr-2 transition-colors duration-150 hover:bg-grayscale-gray02'
              >
                {item.label}
              </BaseButton>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PathfinderMajorSearchSelect;
